import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionNav from '../../../components/layout/SectionNav';
import { researchTabs } from '../../../nav/publicNav';
import { Loader2, GitCompareArrows, X, Search, Plus } from 'lucide-react';
import { useCompareStore } from '../../../state/compareStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';

function SymbolInput() {
  const { inputValue, setInputValue, suggestions, sugLoading, addSymbol, searchSymbols } = useCompareStore();
  const timer = useRef(null);
  const dropdownRef = useRef(null);

  const onChange = (e) => {
    const v = e.target.value;
    setInputValue(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => searchSymbols(v), 300);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      addSymbol(inputValue);
    }
  };

  return (
    <div className="relative w-64">
      <div className="flex items-center gap-1 h-8 px-2 rounded-md border border-border bg-background focus-within:ring-2 focus-within:ring-emerald-500/50">
        <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <input
          value={inputValue}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder="Add symbol (e.g. TCS)..."
          className="flex-1 text-xs bg-transparent outline-none"
        />
      </div>
      {suggestions.length > 0 && (
        <div ref={dropdownRef} className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map(s => (
            <button
              key={s.symbol}
              className="w-full text-left px-3 py-2 text-xs hover:bg-muted/50 flex items-center gap-2"
              onClick={() => { addSymbol(s.symbol); useCompareStore.getState().suggestions = []; useCompareStore.setState({ suggestions: [] }); }}
            >
              <span className="font-semibold">{s.symbol}</span>
              <span className="text-muted-foreground truncate">{s.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ComparisonTable({ companies }) {
  const navigate = useNavigate();
  if (!companies.length) return null;

  const fields = [
    { key: 'symbol', label: 'Symbol' },
    { key: 'sector', label: 'Sector' },
    { key: 'industry', label: 'Industry' },
    { key: 'ai_sector', label: 'AI Sector' },
    { key: 'ai_industry', label: 'AI Industry' },
    { key: 'listing_date', label: 'Listing Date' },
    { key: 'face_value', label: 'Face Value', fmt: v => v != null ? `₹${v}` : '—' },
    { key: 'ai_business_desc', label: 'Description', wrap: true },
    { key: 'promoter', label: 'Promoter %', sh: true },
    { key: 'fii', label: 'FII %', sh: true },
    { key: 'dii', label: 'DII %', sh: true },
    { key: 'public', label: 'Public %', sh: true },
    { key: 'sh_quarter', label: 'SH Quarter', sh: true },
  ];

  function getValue(company, field) {
    if (field.sh) {
      const sh = company.shareholding;
      if (!sh) return '—';
      if (field.key === 'promoter') return sh.promoter_pct != null ? sh.promoter_pct.toFixed(2) + '%' : '—';
      if (field.key === 'fii') return sh.fii_pct != null ? sh.fii_pct.toFixed(2) + '%' : '—';
      if (field.key === 'dii') return sh.dii_pct != null ? sh.dii_pct.toFixed(2) + '%' : '—';
      if (field.key === 'public') return sh.public_pct != null ? sh.public_pct.toFixed(2) + '%' : '—';
      if (field.key === 'sh_quarter') return sh.quarter_end || '—';
      return '—';
    }
    const v = company[field.key];
    if (field.fmt) return field.fmt(v);
    return v || '—';
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-emerald-800 text-white">
            <th className="text-left py-2 px-3 font-semibold text-[10px] uppercase sticky left-0 bg-emerald-800 z-10">Field</th>
            {companies.map(c => (
              <th key={c.isin} className="text-left py-2 px-3 font-semibold text-[10px] uppercase min-w-[160px]">
                <button className="hover:underline" onClick={() => navigate(`/public/india-company/${c.symbol}`)}>
                  {c.company_name}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fields.map(field => (
            <tr key={field.key} className="border-b border-muted/20 hover:bg-muted/20">
              <td className="py-2 px-3 font-medium text-muted-foreground sticky left-0 bg-background z-10 whitespace-nowrap">
                {field.label}
              </td>
              {companies.map(c => (
                <td key={c.isin} className={`py-2 px-3 ${field.wrap ? 'max-w-[200px]' : ''}`}>
                  {field.wrap ? (
                    <p className="line-clamp-3 text-muted-foreground">{getValue(c, field)}</p>
                  ) : (
                    getValue(c, field)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ComparePage() {
  const { symbols, companies, loading, error, removeSymbol, fetchComparison } = useCompareStore();

  return (
    <div className="space-y-4">
      <SectionNav tabs={researchTabs} />
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <GitCompareArrows className="w-5 h-5 text-emerald-500" /> Company Comparison
        </h1>
        <p className="text-xs text-muted-foreground">Compare up to 5 companies side by side</p>
      </div>

      <Card>
        <CardContent className="p-3 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            {symbols.map(s => (
              <Badge key={s} variant="secondary" className="gap-1 pr-1">
                {s}
                <button onClick={() => removeSymbol(s)} className="ml-0.5 hover:text-red-500">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {symbols.length < 5 && <SymbolInput />}
          </div>
          <Button
            size="sm"
            disabled={symbols.length < 2 || loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={fetchComparison}
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <GitCompareArrows className="w-3.5 h-3.5 mr-1" />}
            Compare
          </Button>
        </CardContent>
      </Card>

      {error && (
        <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-950/20 rounded-lg">{error}</div>
      )}

      {companies.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Comparison</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ComparisonTable companies={companies} />
          </CardContent>
        </Card>
      )}

      {!loading && companies.length === 0 && symbols.length === 0 && (
        <div className="text-center py-16 space-y-2">
          <GitCompareArrows className="w-10 h-10 text-muted-foreground/30 mx-auto" />
          <p className="text-sm text-muted-foreground">Add 2-5 company symbols above to get started.</p>
        </div>
      )}
    </div>
  );
}
